/**
 * NE_CRASH v1 — the postMessage contract between this FE (the iframe parent)
 * and the embedded canvas games (Defender / Howzzat / Rocket Boom).
 *
 * Single source of truth for the channel, version, message types, the per-game
 * canvas URL, and the mappers that turn store state into outbound payloads.
 * The canvas is a pure renderer: it only needs enough to draw the right game and
 * the live round — the parent owns locale, player, audio, cashouts, maintenance.
 */
import { environmentConfigurations } from '@configurations/environmentConfigurations'
import { GAME_STATUS } from '@sockets/socketEvents'

export const CANVAS_CHANNEL = 'NE_CRASH'
export const CANVAS_VERSION = 1

// FE -> Canvas
export const OUTBOUND = {
  INIT: 'INIT',
  SCHEDULED: 'SCHEDULED',
  ACCEPT_BET: 'ACCEPT_BET',
  STARTING: 'STARTING',
  RUNNING: 'RUNNING',
  ENDED: 'ENDED',
}

// Canvas -> FE
export const INBOUND = {
  BOOT: 'BOOT',
  LOADING: 'LOADING',
  READY: 'READY',
  ERROR: 'ERROR',
}

// Only the URL differs per game — the canvas itself is a shared renderer that
// speaks this same contract. Driven by env so each environment points at the
// right build.
const CANVAS_URLS = {
  DEFENDER: environmentConfigurations.canvasUrlDefender,
  HOWZZAT: environmentConfigurations.canvasUrlHowzzat,
  ROCKET_BOOM: environmentConfigurations.canvasUrlRocketBoom,
}

export const getCanvasUrl = (gameCode) => CANVAS_URLS[gameCode] || null

/**
 * INIT payload — only what the canvas needs to render the right game visuals.
 * `gameMode` is NORMAL/MINI (derived from the responsive layout mode); `platform`
 * is DESKTOP/TABLET/MOBILE/MINI (the raw responsive layout mode).
 */
export const buildInitPayload = ({ gameCode, gameMode, theme, platform }) => ({
  gameCode,
  gameMode,
  theme,
  platform,
})

/**
 * Map the live round (eventsSlice) to the matching outbound message. Returns
 * null for statuses the canvas does not render (e.g. UNDER_MAINTENANCE — the
 * parent shows that overlay).
 */
export const roundToCanvasMessage = (round) => {
  if (!round) return null
  const { status, roundId, multiplier, isCrashed, duration, acceptBetEndTime } = round
  const serverTime = Date.now()

  switch (status) {
    case GAME_STATUS.SCHEDULED:
      return { type: OUTBOUND.SCHEDULED, payload: { roundId, countdownMs: duration, serverTime } }
    case GAME_STATUS.ACCEPT_BET:
      return {
        type: OUTBOUND.ACCEPT_BET,
        payload: { roundId, countdownMs: duration, acceptBetEndsAt: acceptBetEndTime, serverTime },
      }
    case GAME_STATUS.STARTING:
      return { type: OUTBOUND.STARTING, payload: { roundId, countdownMs: duration, serverTime } }
    case GAME_STATUS.RUNNING:
      return { type: OUTBOUND.RUNNING, payload: { roundId, multiplier, serverTime } }
    case GAME_STATUS.ENDED:
      return {
        type: OUTBOUND.ENDED,
        payload: { roundId, multiplier, isCrashed, nextRoundInMs: duration, serverTime },
      }
    default:
      return null
  }
}
